import type { JSX } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import type { TimerPreset } from '@susdle/shared';
import { PageFrame } from '../shared/components/PageFrame.js';
import { Spread } from '../shared/components/Spread.js';
import { Wordmark } from './components/Wordmark.js';
import { AvatarFrame, EmptyChair } from '../shared/components/AvatarFrame.js';
import { VillageIllustration } from './components/VillageIllustration.js';
import { PrintedCast } from './components/PrintedCast.js';
import { HostControls } from './components/HostControls.js';
import { SignatureBand } from './components/SignatureBand.js';
import { runStartActivation } from './motion.js';
import type { LobbyPlayer, LobbyView } from './types.js';
import './Lobby.css';

export interface LobbyHandlers {
  onFoolToggle: (enabled: boolean) => void;
  onTimerChange: (preset: TimerPreset) => void;
  onStart: () => void;
}

interface LobbyProps {
  view: LobbyView;
  handlers: LobbyHandlers;
}

const MIN_PLAYERS = 3;

type Seat =
  | { kind: 'player'; player: LobbyPlayer }
  | { kind: 'empty'; index: number };

export function Lobby({
  view,
  handlers,
}: LobbyProps): JSX.Element {
  const { t } = useTranslation();

  /* pageFrameHostRef is consumed by runStartActivation, which only needs an
     ancestor of `.susdle-start`. Connection-state UI (banner +
     SchemaMismatchCard) lives at the App shell now, so the Lobby renders
     directly without an early-return branch — the App handles
     schemaMismatch full-replacement before the Lobby ever mounts. */
  const pageFrameHostRef = useRef<HTMLDivElement | null>(null);
  const startBeatRef = useRef<{ kill: () => void } | null>(null);
  const handleStart = useCallback(() => {
    /* Theatrical activation beat: page recedes, Begin lifts onto a
       shadow-3 plate. The route guard waits on this beat before
       transitioning so the player sees the threshold resolve. */
    if (startBeatRef.current) startBeatRef.current.kill();
    startBeatRef.current = runStartActivation(pageFrameHostRef.current, () => {
      handlers.onStart();
    });
  }, [handlers]);

  /* Company grid: players flow in join order, with empty chairs appended
     to keep the page from feeling sparse below the minimum. Above the
     minimum, no empty chairs render — the grid just shows whoever showed
     up. Up to 8 players in 3 columns on desktop / 4 on mobile. */
  const seats = useMemo<Seat[]>(() => {
    const playerSeats: Seat[] = view.players.map((player) => ({
      kind: 'player',
      player,
    }));
    const emptyCount = Math.max(0, MIN_PLAYERS - view.players.length);
    const emptySeats: Seat[] = Array.from({ length: emptyCount }, (_, i) => ({
      kind: 'empty',
      index: i,
    }));
    return [...playerSeats, ...emptySeats];
  }, [view.players]);

  const isHost = view.meId === view.hostId;
  const canStart = view.players.length >= MIN_PLAYERS;
  const hostName = view.players.find((p) => p.id === view.hostId)?.name ?? '';

  return (
    <div ref={pageFrameHostRef} className="susdle-lobby-host">
      <PageFrame>
        <div className="susdle-lobby">
          <Spread
            ariaLabel={t('lobby.spread.ariaLabel')}
            verso={
              <>
                <CompanySection seats={seats} hostId={view.hostId} meId={view.meId} />
                <RoundSection
                  isHost={isHost}
                  foolEnabled={view.settings.foolEnabled}
                  timerPreset={view.settings.timerPreset}
                  onFoolChange={handlers.onFoolToggle}
                  onTimerChange={handlers.onTimerChange}
                />
                <SignatureBand
                  isHost={isHost}
                  hostName={hostName}
                  canStart={canStart}
                  onStart={handleStart}
                />
              </>
            }
            recto={
              <>
                <Wordmark
                  preamble={t('lobby.titleStack.preamble')}
                  title={t('app.title')}
                />
                <VillageIllustration size="recto" />
                <PrintedCast
                  playerCount={view.players.length}
                  foolEnabled={view.settings.foolEnabled}
                  minPlayers={MIN_PLAYERS}
                />
              </>
            }
          />
        </div>
      </PageFrame>
    </div>
  );
}

interface CompanySectionProps {
  seats: Seat[];
  hostId: string;
  meId: string;
}

function CompanySection({ seats, hostId }: CompanySectionProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="susdle-spread__group">
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- Safari/VoiceOver strip list semantics from <ul> when list-style:none is applied (see Lobby.css .susdle-company-grid); explicit role="list" restores them. */}
      <ul
        className="susdle-company-grid"
        role="list"
        aria-label={t('lobby.sections.company')}
      >
        <AnimatePresence initial={false}>
          {seats.map((seat) => {
            if (seat.kind === 'empty') {
              return (
                <li key={`empty-${seat.index}`}>
                  <EmptyChair index={seat.index} />
                </li>
              );
            }
            const { player } = seat;
            return (
              <li key={player.id}>
                <AvatarFrame player={player} isHost={player.id === hostId} />
              </li>
            );
          })}
        </AnimatePresence>
      </ul>
    </div>
  );
}

interface RoundSectionProps {
  isHost: boolean;
  foolEnabled: boolean;
  timerPreset: TimerPreset;
  onFoolChange: (enabled: boolean) => void;
  onTimerChange: (preset: TimerPreset) => void;
}

function RoundSection({
  isHost,
  foolEnabled,
  timerPreset,
  onFoolChange,
  onTimerChange,
}: RoundSectionProps): JSX.Element {
  return (
    <div className="susdle-spread__group">
      <HostControls
        isHost={isHost}
        foolEnabled={foolEnabled}
        timerPreset={timerPreset}
        onFoolChange={onFoolChange}
        onTimerChange={onTimerChange}
      />
    </div>
  );
}
